import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  getListingConfiguration,
  saveListingPreferences,
  saveListingView,
  updateListingView,
  deleteListingView,
} from '../../services/listingPreferencesService';
import CrmAdvancedFilters from './CrmAdvancedFilters';
import CrmColumnManager from './CrmColumnManager';
import CrmDataTable from './CrmDataTable';
import CrmListPage from './CrmListPage';
import CrmSaveViewModal from './CrmSaveViewModal';
import useCrmListState from './useCrmListState';
import { useSettings } from '../../context/SettingsContext';


const FRIENDLY_CONTEXT_FIELDS = {
  legal_entity_id: { label: 'Legal Entity', names: ['legal_entity_name', 'legal_entity_legal_name'], relations: ['legal_entity', 'legalEntity'] },
  brand_id: { label: 'Brand', names: ['brand_name'], relations: ['brand'] },
  business_unit_id: { label: 'Business Unit', names: ['business_unit_name'], relations: ['business_unit', 'businessUnit'] },
  sales_channel_id: { label: 'Sales Channel', names: ['sales_channel_name'], relations: ['sales_channel', 'salesChannel'] },
  storefront_id: { label: 'Storefront', names: ['storefront_name'], relations: ['storefront'] },
};
const AUTO_COLUMN_EXCLUDES = new Set(['id', 'password', 'password_hash', 'token', 'metadata', 'config_json']);
const humanizeColumn = (key) => String(key || '').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
const inferAutoType = (key, sample) => {
  if (/(amount|price|total|subtotal|tax|cost|balance|paid|due)$/i.test(key)) return 'currency';
  if (/(date|_at)$/i.test(key)) return 'date';
  if (typeof sample === 'boolean' || /^is_|^has_|^enabled$/i.test(key)) return 'boolean';
  if (typeof sample === 'number') return 'number';
  return 'text';
};

const friendlyRelationValue = (row, context) => {
  const direct = context.names.map((nameKey) => row?.[nameKey]).find((value) => value != null && value !== '');
  if (direct != null && direct !== '') return direct;
  for (const relationKey of context.relations || []) {
    const relation = row?.[relationKey];
    if (!relation || typeof relation !== 'object') continue;
    const relatedValue = relation.name ?? relation.label ?? relation.legal_name ?? relation.display_name ?? relation.code;
    if (relatedValue != null && relatedValue !== '') return relatedValue;
  }
  return '—';
};

const getFallbackKey = (moduleKey, suffix) => `jdh:crm:list:${moduleKey}:${suffix}`;

const readFallback = (moduleKey, suffix, fallback) => {
  try {
    const value = JSON.parse(localStorage.getItem(getFallbackKey(moduleKey, suffix)) || 'null');
    return value ?? fallback;
  } catch {
    return fallback;
  }
};


const getViewStateKey = (moduleKey, viewId) => `jdh:crm:list:${moduleKey}:view:${String(viewId || 'all')}`;
const readViewState = (moduleKey, viewId) => {
  try { return JSON.parse(localStorage.getItem(getViewStateKey(moduleKey, viewId)) || 'null'); } catch { return null; }
};
const writeViewState = (moduleKey, viewId, value) => {
  try { localStorage.setItem(getViewStateKey(moduleKey, viewId), JSON.stringify(value)); } catch { /* session continues */ }
};
const writeFallback = (moduleKey, suffix, value) => {
  try {
    localStorage.setItem(getFallbackKey(moduleKey, suffix), JSON.stringify(value));
  } catch {
    // The listing remains usable for the current session if browser storage is unavailable.
  }
};

function CrmEntityListPage({
  moduleKey,
  title,
  itemLabel,
  itemPluralLabel,
  items = [],
  columns = [],
  filterDefinitions = [],
  getRowId = (item) => item?.id,
  initialSort,
  initialFilters = {},
  initialPageSize = 20,
  loading = false,
  error = '',
  onRetry,
  onRefresh,
  onCreate,
  createLabel,
  onImport,
  onSettings,
  headerActions,
  summaryItems,
  onExport,
  onExportSelected,
  onDeleteSelected,
  renderBulkActions,
  onRowClick,
  emptyTitle,
  emptyMessage,
  hideSavedViews = false,
  staticViews = null,
  activeStaticView = null,
  onStaticViewChange,
}) {
  const { settings } = useSettings();
  const currencyCode = String(settings?.currency_code || 'INR').toUpperCase();
  const normalizedColumns = useMemo(() => {
    const explicitIds = new Set(columns.map((column) => column.id));
    const discoveredKeys = [...new Set((items || []).flatMap((item) => Object.keys(item || {})))]
      .filter((key) => !explicitIds.has(key) && !AUTO_COLUMN_EXCLUDES.has(key))
      .filter((key) => {
        if (!key.endsWith('_id')) return true;
        const context = FRIENDLY_CONTEXT_FIELDS[key];
        if (context) return true;
        const nameKey = key.replace(/_id$/, '_name');
        return !((items || []).some((item) => item?.[nameKey] != null));
      });

    const discovered = discoveredKeys.map((key) => {
      const sample = (items || []).find((item) => item?.[key] != null)?.[key];
      const context = FRIENDLY_CONTEXT_FIELDS[key];
      return {
        id: key,
        label: context?.label || humanizeColumn(key),
        width: 180,
        minWidth: 72,
        type: inferAutoType(key, sample),
        defaultHidden: true,
        accessor: context
          ? (row) => friendlyRelationValue(row, context)
          : (row) => row?.[key] ?? '',
      };
    });

    return [...columns, ...discovered].map((column) => {
      if (column.type !== 'currency' || !column.label) return column;
      const label = String(column.label);
      if (label.includes(currencyCode) || /\([A-Z]{3}\)/.test(label)) return column;
      return { ...column, label: `${label} (${currencyCode})` };
    });
  }, [columns, currencyCode, items]);

  const hydratedRef = useRef(false);
  const persistenceTimerRef = useRef(null);
  const allViewStateRef = useRef(null);
  const [savedViews, setSavedViews] = useState([]);
  const [columnManagerOpen, setColumnManagerOpen] = useState(false);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [advancedFilterId, setAdvancedFilterId] = useState(null);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [chromeCollapsed, setChromeCollapsed] = useState(false);

  const views = useMemo(() => [
    { id: 'all', label: `All ${itemPluralLabel || `${itemLabel}s`}` },
    ...savedViews.map((view) => ({ id: String(view.id), label: view.name })),
  ], [itemLabel, itemPluralLabel, savedViews]);

  const list = useCrmListState({
    items,
    columns: normalizedColumns,
    views,
    filterDefinitions,
    getRowId,
    initialSort,
    initialFilters,
    initialPageSize,
  });

  const hasStaticViews = Array.isArray(staticViews) && staticViews.length > 0;
  const persistenceViewId = hasStaticViews ? `static:${String(activeStaticView || staticViews?.[0]?.id || 'all')}` : list.activeView;

  const getStateSnapshot = () => ({
    search: list.search,
    filters: list.filters,
    sort: list.sort,
    columnPreferences: list.columnPreferences,
    pageSize: list.pageSize,
  });

  const applyStateSnapshot = (snapshot = {}) => {
    list.setSearch(snapshot.search || '');
    const hasPersistedFilters = snapshot.filters && typeof snapshot.filters === 'object' && Object.keys(snapshot.filters).length > 0;
    list.setFilters(hasPersistedFilters && snapshot.filters && typeof snapshot.filters === 'object' ? snapshot.filters : { ...(initialFilters || {}) });
    list.setSort(snapshot.sort || initialSort || null);
    if (snapshot.columnPreferences) list.applyColumnPreferences(snapshot.columnPreferences);
    if ([20, 50, 100].includes(Number(snapshot.pageSize))) list.setPageSize(Number(snapshot.pageSize));
  };

  useEffect(() => {
    let active = true;
    hydratedRef.current = false;

    const hydrate = async () => {
      let payload;
      try {
        payload = await getListingConfiguration(moduleKey);
      } catch (loadError) {
        payload = {
          preferences: readFallback(moduleKey, 'preferences', {}),
          views: readFallback(moduleKey, 'views', []),
        };
      }
      if (!active) return;

      const fallbackViews = readFallback(moduleKey, 'views', []);
      const fallbackPreferences = readFallback(moduleKey, 'preferences', {});
      const nextViews = Array.isArray(payload?.views) && payload.views.length ? payload.views : fallbackViews;
      const preferences = { ...(payload?.preferences || {}), ...(fallbackPreferences || {}) };
      setSavedViews(nextViews);
      allViewStateRef.current = preferences.allView || preferences.current || null;
      const requestedView = String(preferences.activeView || 'all');
      const validView = requestedView === 'all' || nextViews.some((view) => String(view.id) === requestedView)
        ? requestedView
        : 'all';
      list.setActiveView(validView);
      const savedView = nextViews.find((view) => String(view.id) === String(validView));
      const persistedViewState = readViewState(moduleKey, validView);
      const hydratedState = persistedViewState || (validView === 'all' ? (preferences.allView || preferences.current || {}) : (savedView?.config || preferences.current || {}));
      applyStateSnapshot(hydratedState);
      hydratedRef.current = true;
    };

    hydrate();
    return () => {
      active = false;
      if (persistenceTimerRef.current) clearTimeout(persistenceTimerRef.current);
    };
    // The listing state setters are stable; rehydrate only when the module changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleKey]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    if (persistenceTimerRef.current) clearTimeout(persistenceTimerRef.current);
    persistenceTimerRef.current = setTimeout(() => {
      const current = getStateSnapshot();
      if (!hasStaticViews && list.activeView === 'all') allViewStateRef.current = current;
      writeViewState(moduleKey, persistenceViewId, current);
      const preferences = {
        activeView: hasStaticViews ? (activeStaticView || 'all') : list.activeView,
        current,
        allView: allViewStateRef.current || current,
      };
      writeFallback(moduleKey, 'preferences', preferences);
      saveListingPreferences(moduleKey, preferences).catch(() => {});

      if (!hasStaticViews && String(list.activeView) !== 'all') {
        setSavedViews((previous) => {
          const next = previous.map((view) => String(view.id) === String(list.activeView) ? { ...view, config: current } : view);
          writeFallback(moduleKey, 'views', next);
          return next;
        });
        if (/^\d+$/.test(String(list.activeView))) {
          const activeSavedView = savedViews.find((view) => String(view.id) === String(list.activeView));
          updateListingView(moduleKey, Number(list.activeView), activeSavedView?.name || 'Saved view', current).catch(() => {});
        }
      }
    }, 450);

    return () => {
      if (persistenceTimerRef.current) clearTimeout(persistenceTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    moduleKey,
    list.activeView,
    activeStaticView,
    hasStaticViews,
    persistenceViewId,
    list.search,
    list.filters,
    list.sort,
    list.columnPreferences,
    list.pageSize,
  ]);

  const handleViewChange = (viewId) => {
    if (viewId === list.activeView) return;
    const outgoing = getStateSnapshot();
    writeViewState(moduleKey, list.activeView, outgoing);
    if (list.activeView === 'all') allViewStateRef.current = outgoing;
    else {
      const outgoingView = savedViews.find((view) => String(view.id) === String(list.activeView));
      setSavedViews((previous) => {
        const next = previous.map((view) => String(view.id) === String(list.activeView) ? { ...view, config: outgoing } : view);
        writeFallback(moduleKey, 'views', next);
        return next;
      });
      if (/^\d+$/.test(String(list.activeView))) updateListingView(moduleKey, Number(list.activeView), outgoingView?.name || 'Saved view', outgoing).catch(() => {});
    }
    list.setActiveView(viewId);
    const savedView = savedViews.find((view) => String(view.id) === String(viewId));
    const persistedIncoming = readViewState(moduleKey, viewId);
    const current = persistedIncoming || (viewId === 'all'
      ? (allViewStateRef.current || {})
      : (savedView?.config || getStateSnapshot()));
    const immediatePreferences = {
      activeView: viewId,
      current,
      allView: allViewStateRef.current || current,
    };
    writeFallback(moduleKey, 'preferences', immediatePreferences);
    saveListingPreferences(moduleKey, immediatePreferences).catch(() => {});
    if (viewId === 'all') {
      applyStateSnapshot(allViewStateRef.current || {});
      return;
    }
    if (savedView) applyStateSnapshot(savedView.config);
  };

  const handleSaveView = async (name) => {
    const config = getStateSnapshot();
    try {
      const saved = await saveListingView(moduleKey, name, config);
      if (saved) {
        setSavedViews((previous) => {
          const next = [...previous.filter((view) => String(view.id) !== String(saved.id)), saved];
          writeFallback(moduleKey, 'views', next);
          return next;
        });
        writeViewState(moduleKey, String(saved.id), config);
        list.setActiveView(String(saved.id));
        return;
      }
    } catch (saveError) {
      const fallbackView = {
        id: `local-${Date.now()}`,
        name,
        config,
      };
      setSavedViews((previous) => {
        const withoutDuplicate = previous.filter((view) => view.name.toLowerCase() !== name.toLowerCase());
        const next = [...withoutDuplicate, fallbackView];
        writeFallback(moduleKey, 'views', next);
        return next;
      });
      writeViewState(moduleKey, String(fallbackView.id), config);
      list.setActiveView(String(fallbackView.id));
    }
  };


  const handleDeleteView = async (viewId) => {
    if (String(viewId) === 'all') return;
    const target = savedViews.find((view) => String(view.id) === String(viewId));
    if (!target) return;
    try {
      if (/^\d+$/.test(String(viewId))) await deleteListingView(moduleKey, Number(viewId));
      setSavedViews((previous) => {
        const next = previous.filter((view) => String(view.id) !== String(viewId));
        writeFallback(moduleKey, 'views', next);
        return next;
      });
      if (String(list.activeView) === String(viewId)) {
        list.setActiveView('all');
        applyStateSnapshot(allViewStateRef.current || {});
      }
    } catch (error) {
      console.error('Failed to delete listing view', error);
    }
  };

  const selectedItems = useMemo(() => items.filter((item) => (
    list.selectedIds.includes(getRowId(item))
  )), [getRowId, items, list.selectedIds]);

  const hideColumn = (columnId) => list.applyColumnPreferences({
    ...list.columnPreferences,
    hidden: [...new Set([...list.columnPreferences.hidden, columnId])],
  });

  const togglePinnedColumn = (columnId) => list.applyColumnPreferences({
    ...list.columnPreferences,
    pinned: list.columnPreferences.pinned === columnId ? null : columnId,
  });

  const handleStaticViewChange = (viewId) => {
    if (!hasStaticViews || String(viewId) === String(activeStaticView)) return;
    const outgoingKey = `static:${String(activeStaticView || staticViews?.[0]?.id || 'all')}`;
    writeViewState(moduleKey, outgoingKey, getStateSnapshot());
    onStaticViewChange?.(viewId);
    const incoming = readViewState(moduleKey, `static:${String(viewId)}`);
    if (incoming) applyStateSnapshot(incoming);
  };

  useEffect(() => {
    if (!hydratedRef.current || !hasStaticViews) return;
    const incoming = readViewState(moduleKey, persistenceViewId);
    if (incoming) applyStateSnapshot(incoming);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleKey, persistenceViewId, hasStaticViews]);

  const displayedViews = Array.isArray(staticViews) && staticViews.length ? staticViews : views;
  const displayedActiveView = Array.isArray(staticViews) && staticViews.length ? activeStaticView : list.activeView;
  const displayedViewChange = hasStaticViews ? handleStaticViewChange : handleViewChange;

  return (
    <>
      <CrmListPage
        title={title}
        views={displayedViews}
        activeView={displayedActiveView}
        onViewChange={displayedViewChange}
        onAddView={hasStaticViews ? undefined : () => setSaveViewOpen(true)}
        onDeleteView={hasStaticViews ? undefined : handleDeleteView}
        onCreate={onCreate}
        createLabel={createLabel}
        onImport={onImport}
        onSettings={onSettings}
        headerActions={headerActions}
        summaryItems={summaryItems}
        chromeCollapsed={chromeCollapsed}
        toolbarProps={{
          search: list.search,
          onSearchChange: list.setSearch,
          columns: list.orderedColumns,
          sort: list.sort,
          onSortChange: list.setSort,
          filterDefinitions,
          filters: list.filters,
          onFilterChange: list.setFilter,
          onClearFilter: list.clearFilter,
          activeFilters: list.activeFilterList,
          onClearFilters: list.clearFilters,
          onAdvancedFilters: () => {
            setAdvancedFilterId(null);
            setAdvancedFiltersOpen(true);
          },
          onEditColumns: () => setColumnManagerOpen(true),
          filtersVisible,
          onToggleFilters: () => setFiltersVisible((previous) => !previous),
          chromeCollapsed,
          onToggleChrome: () => setChromeCollapsed((previous) => !previous),
          searchPlaceholder: `Search ${itemPluralLabel || `${itemLabel}s`}`,
        }}
        selectedCount={list.selectedIds.length}
        onExportSelected={onExportSelected ? () => onExportSelected(selectedItems) : undefined}
        onDeleteSelected={onDeleteSelected ? () => onDeleteSelected(list.selectedIds, selectedItems) : undefined}
        bulkExtraActions={renderBulkActions?.(selectedItems, list.selectedIds)}
        hideSavedViews={hideSavedViews}
        footerProps={{
          totalItems: list.processedItems.length,
          itemLabel,
          itemPluralLabel,
          currentPage: list.currentPage,
          totalPages: list.totalPages,
          pageSize: list.pageSize,
          onPageSizeChange: list.setPageSize,
          onPageChange: list.setCurrentPage,
          onRefresh,
          onExport: onExport ? () => onExport(list.processedItems) : undefined,
        }}
      >
        <CrmDataTable
          columns={list.visibleColumns}
          rows={list.pageItems}
          getRowId={getRowId}
          selectedIds={list.selectedIds}
          allSelected={list.allProcessedSelected}
          onToggleSelected={list.toggleSelected}
          onToggleSelectAll={list.toggleSelectAll}
          sort={list.sort}
          onSort={list.setSort}
          pinnedColumnId={list.columnPreferences.pinned}
          onTogglePin={togglePinnedColumn}
          onHideColumn={hideColumn}
          onEditColumns={() => setColumnManagerOpen(true)}
          onFilterColumn={(column) => {
            const definition = filterDefinitions.find((filter) => filter.columnId === column.id);
            setAdvancedFilterId(definition?.id || null);
            setAdvancedFiltersOpen(true);
          }}
          onRowClick={onRowClick}
          onColumnResize={list.setColumnWidth}
          loading={loading}
          error={error}
          onRetry={onRetry}
          emptyTitle={emptyTitle || `No ${itemLabel}s match the current filters.`}
          emptyMessage={emptyMessage || 'Try changing or clearing the filters.'}
          onClearFilters={list.activeFilterList.length || list.search ? () => {
            list.setSearch('');
            list.clearFilters();
          } : undefined}
        />
      </CrmListPage>

      <CrmColumnManager
        open={columnManagerOpen}
        onClose={() => setColumnManagerOpen(false)}
        columns={list.orderedColumns}
        preferences={list.columnPreferences}
        onApply={list.applyColumnPreferences}
        onRestoreDefaults={list.restoreDefaultColumns}
      />

      <CrmAdvancedFilters
        open={advancedFiltersOpen}
        onClose={() => setAdvancedFiltersOpen(false)}
        definitions={filterDefinitions}
        filters={list.filters}
        onApply={list.setFilters}
        onClear={list.clearFilters}
        initialFilterId={advancedFilterId}
      />

      <CrmSaveViewModal
        open={saveViewOpen}
        onClose={() => setSaveViewOpen(false)}
        onSave={handleSaveView}
        entityLabel={itemLabel}
      />
    </>
  );
}

export default CrmEntityListPage;
